import { NotSupportedError, toBigEndian2, toBigEndian4 } from './base';
import { MinHeap } from './min_heap';
import { AppError } from "../common/app_error";
export class ChannelData {
    constructor() {
    }
    toString() {
        return '[version=' + this.version + ', isLast=' + this.isLast + ', type=' + this.type + ', objId=' + this.objId
            + ', segNum=' + this.segNum + ', flag=' + this.flag + ', dataLength=' + this.data.byteLength + ']';
    }
    static parse(data) {
        if (data.byteLength < 8)
            throw new TypeError('At last 8 bytes needed: byteLength=' + data.byteLength);
        const uint8Array = new Uint8Array(data);
        const channelData = new ChannelData();
        let protocol = uint8Array[0];
        channelData.version = ChannelData.fromByte(protocol, 0, 3);
        if (channelData.version > ChannelData.MAX_VERSION)
            throw new NotSupportedError(channelData.version, ChannelData.MAX_VERSION);
        channelData.isLast = ChannelData.fromByte(protocol, 7, 1) === 1;
        channelData.type = ChannelData.fromByte(protocol, 5, 2);
        channelData.objId = ChannelData.fromUint8Array(uint8Array, 1, 4);
        channelData.segNum = ChannelData.fromUint8Array(uint8Array, 5, 2);
        channelData.flag = uint8Array[7];
        channelData.data = new Uint8Array(data, 8);
        // console.debug(channelData.toString())
        return channelData;
    }
    static fromUint8Array(uint8Array, start, length) {
        if (start < 0 || length < 1 || start < 0 || start + length > uint8Array.length)
            throw new Error('Invalid arguments: uint8Array.length=' + uint8Array.length
                + ', start' + start + ', length=' + length);
        let ret = 0;
        length = (length - 1) * 8;
        for (let i = start; length >= 0; ++i, length -= 8) {
            ret += uint8Array[i] << length;
        }
        return ret;
    }
    static fromByte(byte, start, length) {
        if (byte < 0 || byte > 0xff || start < 0 || length < 1 || start + length > 8)
            throw new Error('Invalid arguments: byte=' + byte + ', start' + start + ', length=' + length);
        let ret = 0;
        let mask = 1 << (start + length - 1);
        for (let i = 0; i < length; ++i) {
            ret <<= 1;
            if (mask & byte) {
                ret += 1;
            }
            mask >>= 1;
        }
        return ret;
    }
}
// the big endian is used
// [1(proto), 4(objId), 2(segNum), 1(flag)]
ChannelData.TYPE_BINARY = 0;
ChannelData.TYPE_JSON = 1;
ChannelData.TYPE_MIX = 2;
ChannelData.MAX_VERSION = 0;
ChannelData.MAX_SEG_LENGTH = 1024 * 16;
ChannelData.HEADER_LENGTH = 8;
ChannelData.PROTO_VERSION = 0;
ChannelData.BUFFER_LOW_EVENT = 'bufferedamountlow';
export class ChannelObject {
    constructor(objId, type, flag) {
        this.data = null;
        // the size of the object in bytes
        this.size = 0;
        // the number of segments for the object
        this.count = null;
        this.completed = false;
        this.json = null;
        this.binary = null;
        this.textDecoder = new TextDecoder("utf-8");
        this.objId = objId;
        this.type = type;
        this.flag = flag;
        this.segments = new Map();
    }
    static fromChannelData(channelData) {
        return new ChannelObject(channelData.objId, channelData.type, channelData.flag);
    }
    concatSegments() {
        this.data = new ArrayBuffer(this.size);
        const uint8Array = new Uint8Array(this.data);
        let start = 0;
        for (let i = 0; i < this.count; ++i) {
            const data = this.segments.get(i);
            uint8Array.set(new Uint8Array(data), start);
            start += data.byteLength;
        }
    }
    parseData() {
        let jsonData = null;
        // parse data
        switch (this.type) {
            case ChannelData.TYPE_BINARY:
                this.binary = this.data;
                break;
            case ChannelData.TYPE_MIX:
                let length = ChannelData.fromUint8Array(new Uint8Array(this.data), 0, 2);
                jsonData = new Uint8Array(this.data, 2, length);
                this.binary = new Uint8Array(this.data, length + 2);
                break;
            case ChannelData.TYPE_JSON:
                jsonData = this.data;
                break;
        }
        if (jsonData) {
            this.json = JSON.parse(this.textDecoder.decode(jsonData));
        }
    }
    flush() {
        this.concatSegments();
        this.parseData();
    }
    appendData(channelData) {
        if (this.segments.has(channelData.segNum)) {
            console.error("duplicate segment received: segNum=" + channelData.segNum);
            return;
        }
        this.segments.set(channelData.segNum, channelData.data);
        if (channelData.isLast) {
            this.count = channelData.segNum + 1;
        }
        this.size += channelData.data.byteLength;
        this.completed = this.isComplete();
        if (this.completed) {
            this.flush();
        }
    }
    isComplete() {
        return this.count === this.segments.size;
    }
    toString() {
        return '[objId=' + this.objId + ', type=' + this.type + ', flag=' + this.flag + ', data.length=' +
            this.data?.byteLength + ', size=' + this.size + ', count=' + this.count + ', completed=' + this.completed + ']';
    }
    description() {
        return '[objId=' + this.objId + ', type=' + this.type + ', json=' + this.json + ', binary.length=' +
            this.binary?.byteLength + ']';
    }
    getObj() {
        return { json: this.json, binary: this.binary };
    }
}
ChannelObject.CHANNEL_BUFFER_FULL = 'ChannelBufferFull';
export class ChannelControl {
    constructor(queueSize) {
        this.queueSize = 1;
        if (queueSize)
            this.queueSize = queueSize;
        this.objIds = new MinHeap();
        this.objects = new Map();
    }
    append(channelData) {
        if (this.objects.size >= this.queueSize && !this.objects.has(channelData.objId)) {
            if (this.objIds.peek() < channelData.objId) {
                let objId = this.objIds.pop();
                this.objects.delete(objId);
            }
            else {
                return null;
            }
        }
        let channelObject;
        if (this.objects.has(channelData.objId)) {
            channelObject = this.objects.get(channelData.objId);
        }
        else {
            channelObject = ChannelObject.fromChannelData(channelData);
            this.objIds.push(channelData.objId);
            this.objects.set(channelData.objId, channelObject);
        }
        return channelObject;
    }
    remove(objId) {
        this.objIds.delete(objId);
        this.objects.delete(objId);
    }
    appendData(data) {
        let channelData = ChannelData.parse(data);
        let channelObject = this.append(channelData);
        if (channelObject !== null) {
            channelObject.appendData(channelData);
            if (channelObject.completed) {
                this.remove(channelObject.objId);
            }
        }
        return channelObject;
    }
}
ChannelControl.QUEUE_SIZE = 10;
export class WebRTCChannel {
    constructor(options) {
        this.maxBufferedAmount = 256000;
        this.messageQueue = [];
        this.textEncoder = new TextEncoder();
        this.objId = 0;
        this.connId = options.connId;
        this.channel = options.channel;
        this.channel.binaryType = 'arraybuffer';
        this.channel.bufferedAmountLowThreshold = this.maxBufferedAmount / 2;
        this.onObject = options.onObject;
        this.onOPen = options.onOpen;
        this.onClose = options.onClose;
        this.channel.onmessage = (evt) => {
            this.onChannelMessage(this.channel, evt);
        };
        this.channel.onopen = (evt) => {
            this.onChannelOpen(this.channel, evt);
        };
        this.channel.onclose = (evt) => {
            this.onChannelClose(this.channel, evt);
        };
        this.channel.addEventListener(ChannelData.BUFFER_LOW_EVENT, this.onBufferedAmountLow.bind(this));
    }
    /**
    * Handles the `bufferedamountlow` event, processing the message queue.
    */
    onBufferedAmountLow() {
        console.debug("Buffer has space. Processing the queue...");
        this.processQueue();
    }
    onChannelMessage(channel, event) {
        // console.debug("onChannelMessage: label=" + channel.label + ", connId=" + this.connId)
        let channelControl = channel['ChannelControl'];
        if (!channelControl) {
            let queueSize = null;
            if (!channel.ordered) {
                queueSize = ChannelControl.QUEUE_SIZE;
            }
            channel['ChannelControl'] = channelControl = new ChannelControl(queueSize);
        }
        let channelObject = channelControl.appendData(event.data);
        if (channelObject.completed) {
            this.onObject(channel, channelObject);
        }
    }
    onChannelOpen(channel, event) {
        console.debug("onChannelOpen: label=" + channel.label + ", connId=" + this.connId);
        if (this.onOPen) {
            this.onOPen(channel);
        }
        this.processQueue();
    }
    onChannelClose(channel, event) {
        console.debug("onChannelClose: label=" + channel.label + ", connId=" + this.connId);
        this.onClose(channel);
    }
    checkSend(data) {
        if (this.channel.readyState !== 'open') {
            console.info("DataChannel is not ready.");
            return new Error('The channel is not open.');
        }
        else if (this.channel.bufferedAmount + data.length >= this.maxBufferedAmount) {
            console.info("DataChannel buffer is almost full.");
            const error = new Error('The channel buffer is almost full.');
            error.name = ChannelObject.CHANNEL_BUFFER_FULL;
            return error;
        }
        else {
            return null;
        }
    }
    /**
        * Processes the message queue while the buffer has space.
        */
    processQueue() {
        while (this.messageQueue.length > 0) {
            const nextMessage = this.messageQueue[0];
            if (this.checkSend(nextMessage) !== null) {
                console.info("DataChannel is not open or buffer is full. Stopping the queue processing.");
                break;
            }
            this.sendDataInner(nextMessage);
            this.messageQueue.shift();
            console.debug(`Message sent from queue: ${nextMessage}`);
        }
    }
    sendJsonData(message) {
        const data = this.textEncoder.encode(JSON.stringify(message));
        let error = this.checkSend(data);
        if (error) {
            const totalBytes = this.messageQueue.reduce((sum, msg) => sum + msg.byteLength, 0);
            if (totalBytes + data.byteLength > this.maxBufferedAmount * 0.75) {
                console.warn("messageQueue almost full to drop:", message);
                return error;
            }
            this.messageQueue.push(data);
            return null;
        }
        this.sendDataInner(data);
        return error;
    }
    sendDataInner(data) {
        const requests = this.sliceData(ChannelData.TYPE_JSON, 0, data);
        requests.forEach((request) => {
            this.channel.send(request);
        });
    }
    sliceData(stype, flag, data) {
        // stype: 5-6 bits
        stype = (stype & 0x3) << 5;
        const MaxPayloadLength = ChannelData.MAX_SEG_LENGTH - ChannelData.HEADER_LENGTH;
        let segmentNum = 1;
        let segmentCapacity = ChannelData.MAX_SEG_LENGTH;
        if (data.length > MaxPayloadLength) {
            segmentNum = Math.floor(data.length / MaxPayloadLength) + 1;
        }
        else {
            segmentCapacity = data.length + ChannelData.HEADER_LENGTH;
        }
        let objects = [];
        let first = 0;
        let last = 0;
        let protocol = 0;
        for (let snum = 0; snum < segmentNum; snum++) {
            // protocol: 0-2 bits
            protocol = ChannelData.PROTO_VERSION;
            // segment last: the highest bit
            if (snum + 1 == segmentNum) {
                protocol |= 0x80;
                last = data.length;
            }
            else {
                last = first + MaxPayloadLength;
            }
            // payload
            let payload = data.slice(first, last);
            let segment = new Uint8Array(8 + payload.byteLength);
            // segment type
            protocol |= stype;
            segment[0] = protocol;
            // slice object number
            segment.set(toBigEndian4(this.objId), 1);
            // slice segment number
            segment.set(toBigEndian2(snum), 5);
            // flag
            segment[7] = flag;
            // slice data first:last
            segment.set(payload, 8);
            first = last;
            // append obj to slice
            objects.push(segment);
        }
        this.objId = this.objId + 1;
        return objects;
    }
}
export class ActionMessage {
    constructor(id, act, data, error, obj) {
        this.id = id;
        this.act = act;
        this.data = data;
        this.error = error;
        this.obj = obj;
    }
    static fromChannelObject(obj) {
        let error = null, actionMessage = null;
        if (obj === null || obj.json === null) {
            error = AppError.unknownError({ desc: 'invalid action message' });
            return { error, actionMessage };
        }
        const data = obj.json;
        actionMessage = new ActionMessage(data["id"], data["act"], data["data"], data["error"], obj.getObj());
        if (actionMessage.error) {
            error = new AppError(actionMessage.error);
        }
        return { error, actionMessage };
    }
}
